import * as React from "react";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {none, option, Option} from "ts-option";
import {OnFailure, OnSuccess} from "../../../../store/action/actionHelpers";
import Toast from "../../../helper/Toast";
import {RootState} from "../../../../store/reducer/root/RootState";
import Quote from "../../../../model/quote/Quote";
import {Redirect, withRouter} from "react-router";
import Brand from "../../../../model/brand/Brand";
import CreateQuoteFormView from "./CreateQuoteFormView";
import Spinner from "../../../../hiberUiKit/src/atoms/Spinner/Spinner";
import {store} from "../../../../navigation/ReduxProvider";
import {fetchLead} from "../../../../store/action/lead/lead";
import {fetchBrands} from "../../../../store/action/brand/brand";
import PartnerBrand from "../../../../model/partnerBrand/PartnerBrand";
import {getIndividualContact} from "../../../../store/view/contact";
import Routes from "../../../../navigation/route/Route";
import {getLeadByIdOrRefNo} from "../../../../store/view/lead";
import {api} from "../../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";

interface ComponentProps {
	/**
	 * lead reference number
	 */
	leadReferenceNumber: string;
	/**
	 * On success
	 */
	onSuccess: () => any;
	/**
	 * On no data action
	 */
	onNoData: () => any;
}

interface ReduxProps {
	/**
	 * Brand
	 */
	brand: Option<Brand>
	/**
	 * Vat value
	 */
	vat: Option<number>,
	/**
	 * Partner brand
	 */
	partnerBrand: Option<PartnerBrand>,
	/**
	 * Indicates, if fetching is not finished
	 */
	isFetchingInProgress: boolean,
	/**
	 * Lead identifier
	 */
	leadId: Option<string>
}

interface DispatchProps {
	/**
	 * Fetch lead
	 * @param {string} leadId
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchLead: (leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch brands
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchBrands: (onSuccess: OnSuccess, onFailure: OnFailure) => void;
}

interface State {
	/**
	 * Submitting indicator
	 */
	isSubmitting: boolean;
	/**
	 * Data fetching in progress
	 */
	dataFetching: boolean;
	/**
	 * Data fetched
	 */
	dataFetched: boolean;
}

type Props = ReduxProps & ComponentProps & DispatchProps;

/**
 * Create quote form view
 */
class CreateQuoteForm extends React.PureComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {isSubmitting: false, dataFetched: false, dataFetching: false};
		this.onSubmit = this.onSubmit.bind(this);
		this.checkBrandExists = this.checkBrandExists.bind(this);
	}

	componentDidMount() {
		this.checkBrandExists();
	}

	render() {
		const {brand, vat, partnerBrand, isFetchingInProgress, leadId} = this.props;
		if (brand.isDefined && vat.isDefined && !isFetchingInProgress && leadId.isDefined) {
			return (
				<CreateQuoteFormView
					leadId={leadId.get}
					brand={brand.get}
					partnerBrand={partnerBrand}
					vat={vat.get}
					onSubmit={this.onSubmit}
					isSubmitting={this.state.isSubmitting}/>
			)
		}
		else if (this.props.leadId.isEmpty) {
			return <Redirect to={Routes.LeadsList}/>
		}
		else {
			return <Spinner/>
		}
	}

	/**
	 * If no brand try fetch lead and brands again. If there is still no brand, execute callback method.
	 */
	checkBrandExists() {
		if (this.props.brand.isEmpty) {
			if (store.getState().brand.brands.isDefined && this.props.leadId.isEmpty) {
				if (this.state.dataFetched) {
					this.props.onNoData();
				}
			}
			else if (!this.state.dataFetching) {
				this.setState({dataFetching: true},
					() => this.props.fetchLead(
						this.props.leadId.get,
						() => this.props.fetchBrands(
							() => this.setState({dataFetched: true, dataFetching: false}),
							() => this.props.onNoData()
						),
						() => this.props.onNoData())
				);
			}
		}
	}

	/**
	 * On finish quote creation.
	 */
	private onSubmit(quote: Quote) {
		this.setState({isSubmitting: true}, () => {
			api.instance.quoteApi.createQuote(quote)
				.then(() =>
					this.setState({isSubmitting: false}, () => {
						Toast.success("Quote created successfully", "Quote creation");
						this.props.onSuccess();
					}))
				.catch((error) =>
					this.setState({isSubmitting: false}, () => {
						notifyBugsnagAndShowToast(error, "Quote creation", "Problem with creating quote");
					})
				);
		});
	}
}

function mapStateToProps(state: RootState, props: Props) {
	const lead = getLeadByIdOrRefNo(props.leadReferenceNumber, state);
	let brand: Brand;
	let partnerBrand: PartnerBrand;
	let isFetchingInProgress = true;
	if (lead.isDefined) {
		const contact = getIndividualContact(lead.get.data.principalContactId, state);
		if (contact.isDefined) {
			brand = state.brand.brands.getOrElse([]).filter(b => b.id === contact.get.data.brandId)[0];
			partnerBrand = state.partnerBrand.partnerBrandsMap[contact.get.data.partnerBrandId.orNull]
			if (contact.get.data.partnerBrandId.isEmpty || partnerBrand) {
				isFetchingInProgress = false
			}
		}
	}

	return {
		leadId: lead.map(l => l.id),
		vat: brand ? option(brand.vatRate) : none,
		brand: brand ? option(brand) : none,
		partnerBrand: option(partnerBrand),
		isFetchingInProgress
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchLead: (leadId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchLead(leadId, onSuccess, onFailure)),
		fetchBrands: (onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(fetchBrands(onSuccess, onFailure))
	};
}

export default withRouter(
	connect<ReduxProps, DispatchProps, ComponentProps>(mapStateToProps, mapDispatchToProps)(CreateQuoteForm));
